import React from 'react';
import PageHeader from '../components/PageHeader';
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import "../styles/common-page.css";
import DownloadCard from '../components/DownloadCard';
import schemeImage from "../assets/images/scheme.png";
import VillageIllustration from '../components/VillageIllustration';

const SECTIONS_DATA = [
  {
    id: 1,
    mr: "१) सुशासनयुक्त पंचायत",
    en: "1) Good Governance Panchayat",
    gradient: "linear-gradient(135deg, #00B8DB 0%, #004A5D 100%)"
  },
  {
    id: 2,
    mr: "२) गावपातळीवरील संस्था सक्षमीकरण",
    en: "2) Empowerment of Institutions at the Village Level",
    gradient: "linear-gradient(135deg, #FF8027 0%, #F54900 100%)"
  },
  {
    id: 3,
    mr: "३) लोकसहभाग व श्रमदान माध्यमातून लोकचळवळ निर्माण करणे",
    en: "3) Creating Movement through Public Participation and Labor Donation",
    gradient: "linear-gradient(135deg, #00C950 0%, #006D29 100%)"
  },
  {
    id: 4,
    mr: "४) जल समृद्ध, स्वच्छ व हरित गाव",
    en: "4) Water Resources, Clean and Green Village",
    gradient: "linear-gradient(135deg, #2B7FFF 0%, #002371 100%)"
  },
  {
    id: 5,
    mr: "५) मनरेगा व इतर योजनांशी अभिसरण",
    en: "5) Convergence of MGNREGA and Other Schemes",
    gradient: "linear-gradient(135deg, #B77224 0%, #573000 100%)"
  },
  {
    id: 6,
    mr: "६) सक्षम पंचायत (स्वनिधी व लोकनियोजन)",
    en: "6) Self Panchayat (Self-reliant and Lifestyle)",
    gradient: "linear-gradient(135deg, #EC5042 0%, #B7131A 100%)"
  },
  {
    id: 7,
    mr: "७) उपजीविका विकास, सामाजिक न्याय",
    en: "7) Livelihood Development, Social Justice",
    gradient: "linear-gradient(135deg, #00C950 0%, #006D29 100%)"
  }
];

const GradientSection = ({ textMr, textEn, gradient, className }) => {
  const { i18n } = useTranslation();
  const text = i18n.language === 'en' ? textEn : textMr;

  return (
    <div 
      className={`w-full min-h-[52px] rounded-[28px] flex items-center px-4 sm:px-7 py-3 sm:py-0 text-white text-[14px] font-bold transition-all hover:shadow-lg ${className}`}
      style={{
        background: gradient,
        letterSpacing: "0.25px",
        lineHeight: "20px"
      }}
    >
      {text}
    </div>
  );
};

const SamruddhaPanchayat = () => {
  const { i18n } = useTranslation();

  // Language-specific text content
  const content = {
    en: {
      homepage: 'Homepage',
      title: 'Chief Minister Samruddha Panchayat Raj Abhiyan',
      description: 'The "Chief Minister Samruddha Panchayat Raj Abhiyan" award campaign is being implemented from the financial year 2025-26 to provide incentives at four levels - Taluka, District, Division, and State - for Gram Panchayats, Panchayat Samitis, and Zilla Parishads, which primarily function as local self-government institutions in rural areas.',
      footerAlt: 'Footer Image'
    },
    mr: {
      homepage: 'मुख्यपृष्ठ',
      title: 'मुख्यमंत्री समृद्ध पंचायत राज अभियान',
      description: 'ग्रामीण भागात स्थानिक स्वराज्य संस्था म्हणून प्रामुख्याने काम करणाऱ्या ग्रामपंचायत, पंचायत समिती व जिल्हा परिषदांसाठी सन २०२५-२६ या आर्थिक वर्षापासून तालुका, जिल्हा, विभाग व राज्य अशा चार स्तरांवर प्रोत्साहन देण्याकरिता "मुख्यमंत्री समृद्ध पंचायतराज अभियान" हे पुरस्कार अभियान राबविण्यात येत आहे.',
      footerAlt: 'तळ चित्र'
    }
  };

  const currentContent = content[i18n.language] || content.mr;

  return (
    <>
      <div className="bg-image-section">
        <div className="flex flex-col gap-4 section-padding">
          <PageHeader
            breadcrumb={[
              <Link key="home" to="/">{currentContent.homepage}</Link>,
              <span key="samruddha">{currentContent.title}</span>
            ]}
            title={currentContent.title}
            description={currentContent.description}
          />

          <div className="border border-[#DDDDDD] bg-white rounded-md">
            <div className="flex flex-col lg:flex-row">
              {/* Left side - Image */}
              <div className="w-full lg:w-1/2 p-4 sm:p-6">
                <div className='w-full'>
                  <img loading="lazy" 
                    src={schemeImage} 
                    alt="Chief Minister Samruddha Panchayat Raj Abhiyan Scheme" 
                    className="w-full object-contain" 
                  />
                </div>
              </div>

              {/* Right side - Gradient Sections */}
              <div className="w-full lg:w-1/2 p-4 sm:p-6 lg:pl-0">
                <div className="flex flex-col gap-2 sm:gap-[6px]">
                  {SECTIONS_DATA.map((section) => (
                    <GradientSection
                      key={section.id}
                      textMr={section.mr}
                      textEn={section.en}
                      gradient={section.gradient}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex flex-col gap-6">
              {/* White Background Card */}
              <DownloadCard
                title="मुख्यमंत्री समृद्ध पंचायतराज अभियान (GR)"
                titleEn="Chief Minister Samruddha Panchayat Raj Abhiyan (GR)"
                description="मुख्यमंत्री समृद्ध पंचायतराज अभियानाबद्दलची माहिती डाउनलोड करा, ज्यामध्ये दानशूर व्यक्तींची यादी आहे, ज्यांनी त्यांच्या उदार देणग्यांद्वारे ग्रामपंचायतीच्या विकासात योगदान दिले आहे. या यादीत तुम्हाला आवश्यक सर्व माहिती मिळेल."
                descriptionEn="Download information about the Chief Minister Samruddha Panchayat Raj Abhiyan (GR), which includes a list of generous individuals who have contributed to the development of the Gram Panchayat through their generous donations. This list contains all the necessary information you need."
                downloadLink="/assets/forms/Samrudha Gram Panchyat Abhiyan GR.pdf"
                buttonLabel="डाउनलोड"
                buttonLabelEn="Download"
              />

              {/* Gradient Background Card */}
              <DownloadCard
                title="गावाची कामगिरी"
                titleEn="Village Performance Report"
                description="मुख्यमंत्री समृद्ध पंचायतराज अभियानाची कार्यक्षमता दर्शविणारी अहवाल डाउनलोड करा. या अहवालात दानशूर व्यक्तींची यादी समाविष्ट आहे, ज्यांनी त्यांच्या उदार देणग्यांद्वारे ग्रामपंचायतीच्या विकासात महत्त्वपूर्ण योगदान दिले आहे. या यादीत तुम्हाला आवश्यक सर्व माहिती उपलब्ध असेल."
                descriptionEn="Download the performance report of the Chief Minister Samruddha Panchayat Raj Abhiyan. This report includes a list of generous individuals who have made significant contributions to the development of the Gram Panchayat through their generous donations. This list will have all the necessary information you need."
                downloadLink="/documents/village-performance-report.pdf"
                buttonLabel="डाउनलोड"
                buttonLabelEn="Download"
                backgroundColor="linear-gradient(135deg, #2B7FFF 0%, #002371 100%)"
                titleStyle={{
                  background: 'linear-gradient(179.95deg, #EDF7E6 45.48%, #FFFFFF 109.58%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
                descriptionStyle={{ color: 'white' }}
                iconClassName="filter brightness-0 invert"
              />
            </div>
          </div>

        </div>
      </div>
      <VillageIllustration />
    </>
  );
};

export default SamruddhaPanchayat;